/**
 * Auth Service – all auth via Inference API (no Supabase client in frontend).
 * Tokens stored in localStorage; Bearer sent to API for /auth/me and user-scoped endpoints.
 */

import { API_BASE_URL } from '../config'

const STORAGE_KEY = 'propdna_auth'
const TIER_CACHE_KEY = 'propdna_tier_cache' // synced with usePremium; clear on signOut so next user doesn't see stale tier

const _listeners = new Set()

function decodeJwtPayload(token) {
  try {
    if (!token || typeof token !== 'string') return null
    const parts = token.split('.')
    if (parts.length < 2) return null
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4)
    const json = atob(padded)
    return JSON.parse(json)
  } catch {
    return null
  }
}

function deriveUserFromToken(accessToken) {
  const payload = decodeJwtPayload(accessToken)
  if (!payload) return null
  const id = payload.sub ? String(payload.sub) : null
  const email = payload.email ? String(payload.email) : null
  if (!id) return null
  return { id, email }
}

function getStoredSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function setStoredSession(data) {
  try {
    if (data) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
    const user = data?.user || null
    _listeners.forEach((cb) => { try { cb(user) } catch (_) {} })
  } catch (e) {
    console.error('Auth storage write failed:', e)
  }
}

function clearStoredSession() {
  setStoredSession(null)
}

/**
 * Get current access token (for API Authorization header).
 * @returns {string|null}
 */
export function getAccessToken() {
  const s = getStoredSession()
  return s?.access_token || null
}

/**
 * Get current user session (synthetic: access_token + user from storage).
 * @returns {Promise<Object|null>} Session or null
 */
export const getSession = async () => {
  const s = getStoredSession()
  if (!s?.access_token) return null
  return {
    access_token: s.access_token,
    refresh_token: s.refresh_token,
    user: s.user || null,
  }
}

/**
 * Get current user. Uses stored user if present; otherwise fetches /auth/me and caches.
 * @returns {Promise<Object|null>} User { id, email } or null
 */
export const getCurrentUser = async () => {
  const s = getStoredSession()
  if (!s?.access_token) return null
  if (s.user?.id) return s.user
  const derivedUser = deriveUserFromToken(s.access_token)
  if (derivedUser) {
    setStoredSession({ ...s, user: derivedUser })
    return derivedUser
  }
  try {
    const r = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${s.access_token}`,
      },
    })
    if (!r.ok) return null
    const me = await r.json()
    const user = me?.user ? { id: me.user.id, email: me.user.email } : null
    if (user) setStoredSession({ ...s, user })
    return user
  } catch {
    return null
  }
}

/**
 * Sign up (proxy via Inference API).
 * Sends device fingerprint when available for analytics.
 * @returns {Promise<Object>} { user, session, error }
 */
export const signUp = async (email, password) => {
  try {
    const { getFingerprint } = await import('../utils/fingerprint')
    const fingerprint = await getFingerprint().catch(() => null)
    const body = { email, password }
    if (fingerprint) body.fingerprint = fingerprint
    const shareRef = localStorage.getItem('share_ref')
    if (shareRef) body.shareRef = shareRef
    const r = await fetch(`${API_BASE_URL}/api/v1/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const data = await r.json().catch(() => ({}))
    if (!r.ok) {
      return { user: null, session: null, error: { message: data.detail || data.msg || 'Sign up failed' } }
    }
    // Clear share attribution after successful signup so it doesn't persist indefinitely
    localStorage.removeItem('share_ref')
    const payload = { 
      access_token: data.access_token, 
      refresh_token: data.refresh_token, 
      user: data.user,
      effectiveTier: data.effectiveTier || 'free'
    }
    setStoredSession(payload)

    // Trigger a background fetch of the full profile to pre-populate the cache
    // including saved searches and homeowner property
    fetch(`${API_BASE_URL}/api/v1/auth/me`, {
      headers: { 'Authorization': `Bearer ${data.access_token}` }
    }).then(async (r) => {
      if (r.ok) {
        const me = await r.json()
        sessionStorage.setItem(TIER_CACHE_KEY, JSON.stringify({
          effectiveTier: me.effectiveTier || 'free',
          homeownerProperty: me.homeowner_property || null,
          savedSearches: me.saved_searches || [],
          expiresAt: Date.now() + (5 * 60 * 1000),
        }))
      }
    }).catch(() => {})

    return { user: data.user, session: payload, error: null }
  } catch (e) {
    return { user: null, session: null, error: { message: e.message || 'Sign up failed' } }
  }
}

/**
 * Sign in (proxy via Inference API).
 * @returns {Promise<Object>} { user, session, error }
 */
export const signIn = async (email, password) => {
  try {
    const r = await fetch(`${API_BASE_URL}/api/v1/auth/signin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    const data = await r.json().catch(() => ({}))
    if (!r.ok) {
      return { user: null, session: null, error: { message: data.detail || data.msg || 'Sign in failed' } }
    }
    const payload = { 
      access_token: data.access_token, 
      refresh_token: data.refresh_token, 
      user: data.user,
      effectiveTier: data.effectiveTier || 'free'
    }
    setStoredSession(payload)

    // Trigger a background fetch of the full profile to pre-populate the cache
    // including saved searches and homeowner property
    fetch(`${API_BASE_URL}/api/v1/auth/me`, {
      headers: { 'Authorization': `Bearer ${data.access_token}` }
    }).then(async (r) => {
      if (r.ok) {
        const me = await r.json()
        sessionStorage.setItem(TIER_CACHE_KEY, JSON.stringify({
          effectiveTier: me.effectiveTier || 'free',
          homeownerProperty: me.homeowner_property || null,
          savedSearches: me.saved_searches || [],
          expiresAt: Date.now() + (5 * 60 * 1000),
        }))
      }
    }).catch(() => {})

    return { user: data.user, session: payload, error: null }
  } catch (e) {
    return { user: null, session: null, error: { message: e.message || 'Sign in failed' } }
  }
}

/**
 * Sign in with magic link – not supported when using API-only auth.
 * @returns {Promise<Object>} { error }
 */
export const signInWithMagicLink = async () => {
  return { error: { message: 'Magic link is not available. Use email and password.' } }
}

/**
 * Request password reset email (proxy via Inference API → Supabase GoTrue → Mailgun).
 * @param {string} email - User email
 * @param {string} [redirectTo] - Full URL for the link in the email (e.g. origin + '/auth/callback'). If omitted, uses current origin + '/auth/callback'.
 * @returns {Promise<Object>} { success, error }
 */
export const requestResetEmail = async (email, redirectTo) => {
  const redirect = redirectTo || (typeof window !== 'undefined' ? `${window.location.origin}/auth/reset-callback` : undefined)
  try {
    const r = await fetch(`${API_BASE_URL}/api/v1/auth/recover`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, redirect_to: redirect || undefined }),
    })
    const data = await r.json().catch(() => ({}))
    if (!r.ok) {
      return { success: false, error: { message: data.detail || data.msg || 'Failed to send reset email' } }
    }
    return { success: true, error: null }
  } catch (e) {
    return { success: false, error: { message: e.message || 'Failed to send reset email' } }
  }
}

/**
 * Update password (proxy via Inference API; requires current session).
 * @param {string} newPassword
 * @returns {Promise<Object>} { success, error }
 */
export const updatePassword = async (newPassword) => {
  const token = getAccessToken()
  if (!token) return { success: false, error: { message: 'Not signed in' } }
  try {
    const r = await fetch(`${API_BASE_URL}/api/v1/auth/update-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ password: newPassword }),
    })
    const data = await r.json().catch(() => ({}))
    if (!r.ok) return { success: false, error: { message: data.detail || 'Failed to update password' } }
    return { success: true, error: null }
  } catch (e) {
    return { success: false, error: { message: e.message || 'Failed to update password' } }
  }
}

/**
 * Sign out (clear local tokens and tier cache).
 * @returns {Promise<Object>} { error }
 */
export const signOut = async () => {
  clearStoredSession()
  try {
    sessionStorage.removeItem(TIER_CACHE_KEY)
  } catch (_) {}
  return { error: null }
}

/**
 * Subscribe to auth state changes (sign in / sign out).
 * @param {Function} callback - (user) => void
 * @returns {Function} Unsubscribe
 */
export const onAuthStateChange = (callback) => {
  _listeners.add(callback)
  const s = getStoredSession()
  try { callback(s?.user || null) } catch (_) {}
  return () => { _listeners.delete(callback) }
}

/**
 * Set session from tokens (e.g. OAuth or magic link callback with hash params).
 * @param {string} access_token
 * @param {string} [refresh_token]
 * @param {Object} [user] - { id, email }
 */
export function setSessionFromTokens(access_token, refresh_token, user) {
  if (!access_token) return
  const resolvedUser = user || deriveUserFromToken(access_token)
  setStoredSession({ access_token, refresh_token: refresh_token || null, user: resolvedUser || null })
}

const POST_AUTH_REDIRECT_MAX_AGE_MS = 10 * 60 * 1000

/**
 * Read and clear the URL saved before OAuth (Google, etc.).
 * SessionStorage is preferred; localStorage is a fallback (same tab usually keeps both).
 * @returns {string|null} Path + query starting with '/', or null
 */
export function consumePostAuthRedirect() {
  let returnTo = null
  try {
    const saved = sessionStorage.getItem('post_auth_redirect')
    if (saved && saved.startsWith('/')) {
      returnTo = saved
    } else {
      const lsRaw = localStorage.getItem('post_auth_redirect')
      if (lsRaw) {
        const parsed = JSON.parse(lsRaw)
        if (parsed?.url?.startsWith('/') && Date.now() - (parsed.ts || 0) < POST_AUTH_REDIRECT_MAX_AGE_MS) {
          returnTo = parsed.url
        }
      }
    }
  } catch (_) {}
  try {
    sessionStorage.removeItem('post_auth_redirect')
  } catch (_) {}
  try {
    localStorage.removeItem('post_auth_redirect')
  } catch (_) {}
  return returnTo
}
